import React, { useState, useEffect } from 'react';
import contextAnalyticsService from '../../../services/contextAnalyticsService';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const AgentActivityTable = ({ dateRange, selectedAgent, showNotification }) => {
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchActivities();
  }, [dateRange, selectedAgent, page]);

  const fetchActivities = async () => {
    try {
      setLoading(true);

      const filters = buildFilters();
      filters.limit = limit;
      filters.skip = page * limit;

      const response = await contextAnalyticsService.getAgentActivity(filters);

      if (response.success) {
        // Normalize department field to ensure it's always a string
        const normalizedActivities = response.data.queries.map(activity => ({
          ...activity,
          agentId: activity.agentId ? {
            ...activity.agentId,
            department: typeof activity.agentId.department === 'object'
              ? (activity.agentId.department.name || Object.keys(activity.agentId.department)[0] || 'N/A')
              : (activity.agentId.department || 'N/A')
          } : null
        }));
        setActivities(normalizedActivities);
        setTotal(response.data.total);
      }
    } catch (error) {
      logger.error('Error fetching agent activities:', error);
      showNotification?.('Failed to load agent activities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const buildFilters = () => {
    const filters = {};
    const now = Date.now();

    switch (dateRange) {
      case 'today': {
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);
        filters.startDate = today.toISOString();
        break;
      }
      case 'week':
        filters.startDate = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case 'month':
        filters.startDate = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
        break;
      default:
        break;
    }

    if (selectedAgent) {
      filters.agentId = selectedAgent;
    }

    return filters;
  };

  const toggleRow = (queryId) => {
    setExpandedRow(expandedRow === queryId ? null : queryId);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getStatusBadge = (status) => {
    const colors = {
      success: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      partial: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      fallback: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    };

    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${colors[status] || colors.success}`}>
        {status}
      </span>
    );
  };

  const filteredActivities = activities.filter(activity =>
    searchQuery === '' ||
    activity.queryText?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    activity.agentId?.agentName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Agent Activity Log
          </h3>

          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search queries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md leading-5 bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {filteredActivities.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-[#111111]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Query Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Query
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Cache
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#1a1a1a] divide-y divide-gray-200 dark:divide-gray-800">
              {filteredActivities.map((activity) => (
                <React.Fragment key={activity._id}>
                  <tr className="hover:bg-gray-50 dark:hover:bg-[#222222]">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatTimestamp(activity.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {activity.agentId?.agentName || 'N/A'}
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {typeof activity.agentId?.department === 'string' ? activity.agentId.department : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {activity.queryType}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-md truncate">
                      {activity.queryText || activity.params?.identifier || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {activity.duration ? `${Math.round(activity.duration)}ms` : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {activity.cacheHit ? (
                        <div className="flex items-center text-green-600 dark:text-green-400">
                          <CheckCircleIcon className="h-5 w-5 mr-1" />
                          Hit
                        </div>
                      ) : (
                        <div className="flex items-center text-gray-400">
                          <XCircleIcon className="h-5 w-5 mr-1" />
                          Miss
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(activity.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => toggleRow(activity._id)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        {expandedRow === activity._id ? (
                          <ChevronUpIcon className="h-5 w-5" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5" />
                        )}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Row */}
                  {expandedRow === activity._id && (
                    <tr>
                      <td colSpan="8" className="px-6 py-4 bg-gray-50 dark:bg-[#111111]">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Query Details</h4>
                            <dl className="space-y-1">
                              <div>
                                <dt className="text-gray-500 dark:text-gray-400">Full Query:</dt>
                                <dd className="text-gray-900 dark:text-white">{activity.queryText || 'N/A'}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-500 dark:text-gray-400">Data Sources:</dt>
                                <dd className="text-gray-900 dark:text-white">
                                  {activity.dataSources?.join(', ') || 'N/A'}
                                </dd>
                              </div>
                              {activity.params && (
                                <div>
                                  <dt className="text-gray-500 dark:text-gray-400">Parameters:</dt>
                                  <dd className="text-gray-900 dark:text-white font-mono text-xs">
                                    {JSON.stringify(activity.params, null, 2)}
                                  </dd>
                                </div>
                              )}
                            </dl>
                          </div>

                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Performance Metrics</h4>
                            <dl className="space-y-1">
                              {activity.ragMetrics && (
                                <>
                                  <div>
                                    <dt className="text-gray-500 dark:text-gray-400">Embedding Time:</dt>
                                    <dd className="text-gray-900 dark:text-white">
                                      {activity.ragMetrics.embeddingTime}ms
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="text-gray-500 dark:text-gray-400">Vector Search Time:</dt>
                                    <dd className="text-gray-900 dark:text-white">
                                      {activity.ragMetrics.vectorSearchTime}ms
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="text-gray-500 dark:text-gray-400">Departments Searched:</dt>
                                    <dd className="text-gray-900 dark:text-white">
                                      {activity.ragMetrics.departmentsSearched?.join(', ') || 'N/A'}
                                    </dd>
                                  </div>
                                </>
                              )}
                              {activity.resultMetadata && (
                                <>
                                  <div>
                                    <dt className="text-gray-500 dark:text-gray-400">Total Chunks:</dt>
                                    <dd className="text-gray-900 dark:text-white">
                                      {activity.resultMetadata.totalChunks}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="text-gray-500 dark:text-gray-400">Total Tokens:</dt>
                                    <dd className="text-gray-900 dark:text-white">
                                      {activity.resultMetadata.totalTokens}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="text-gray-500 dark:text-gray-400">Cost:</dt>
                                    <dd className="text-gray-900 dark:text-white">
                                      ${typeof activity.resultMetadata.estimatedCost === 'number'
                                        ? activity.resultMetadata.estimatedCost.toFixed(6)
                                        : activity.resultMetadata.estimatedCost || '0.000000'}
                                    </dd>
                                  </div>
                                </>
                              )}
                            </dl>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              No activity data available for the selected period.
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-[#222222] hover:bg-gray-50 dark:hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * limit >= total}
              className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-[#222222] hover:bg-gray-50 dark:hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentActivityTable;
